/**
 * Help while a ticket is written (#1172): similar tickets for the title being
 * typed, and a type/category hint from the description.
 *
 * Both are hints. Nothing here changes the draft on its own; failures are
 * logged and otherwise invisible — the panel works exactly as before when
 * the BFF or the model does not answer.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { logWarn } from '@/lib/logger'
import type { AllowedTicketTypes } from '@/services/role-management-service'
import {
  bugReportService,
  type SimilarTicket,
  type TicketCategory,
  type TicketSuggestion,
  type TicketType,
} from '@/services/bug-report-service'

/** Fewer characters match almost anything. */
export const SIMILAR_MIN_CHARS = 4
export const SIMILAR_DEBOUNCE_MS = 300
/** The model's own estimate; below it the hint would be a coin toss. */
export const SUGGESTION_MIN_CONFIDENCE = 0.6
/** Leaving the title asks for a hint only once it says enough on its own. */
export const SUGGESTION_TITLE_ONLY_CHARS = 20

export function similarQuery(title: string): string | null {
  const query = title.trim().replace(/\s+/g, ' ')
  return query.length >= SIMILAR_MIN_CHARS ? query : null
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

/** Debounced similar-ticket search for the title; stale answers are dropped. */
export function useSimilarTickets(title: string, type: TicketType, enabled: boolean): SimilarTicket[] {
  const [tickets, setTickets] = useState<SimilarTicket[]>([])
  const query = enabled ? similarQuery(title) : null

  useEffect(() => {
    if (!query) {
      setTickets([])
      return
    }
    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      bugReportService
        .findSimilar(query, type, controller.signal)
        .then((found) => {
          if (!controller.signal.aborted) setTickets(found)
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted || isAbort(error)) return
          logWarn({ context: 'TicketPanel.similar', error: String(error) }, 'Similar tickets unavailable')
          setTickets([])
        })
    }, SIMILAR_DEBOUNCE_MS)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [query, type])

  return tickets
}

export type SuggestionHint =
  | { kind: 'type'; type: TicketType; category: TicketCategory | null }
  | { kind: 'category'; category: TicketCategory }

export interface DraftClassification {
  type: TicketType
  category: TicketCategory | ''
  /** The person chose the type / category themselves: no hint about it. */
  typeTouched: boolean
  categoryTouched: boolean
  /** #1544: a type the person's roles switched off is never suggested. */
  allowedTypes?: AllowedTicketTypes
}

/** What the hint offers for this draft, or nothing. A different type is
 *  offered with its category; a category alone only while none is chosen. */
export function hintFor(suggestion: TicketSuggestion | null, draft: DraftClassification): SuggestionHint | null {
  if (!suggestion || suggestion.confidence < SUGGESTION_MIN_CONFIDENCE) return null
  const category = suggestion.category === 'other' ? null : suggestion.category
  if (suggestion.type !== draft.type) {
    if (draft.typeTouched || draft.allowedTypes?.[suggestion.type] === false) return null
    return { kind: 'type', type: suggestion.type, category }
  }
  if (!category || draft.categoryTouched || draft.category !== '') return null
  return { kind: 'category', category }
}

/** Asks for a suggestion when called (on blur), at most once per text. */
export function useTicketSuggestion() {
  const [suggestion, setSuggestion] = useState<TicketSuggestion | null>(null)
  const askedRef = useRef<string | null>(null)
  const controllerRef = useRef<AbortController | null>(null)

  const request = useCallback((title: string, description: string) => {
    const key = `${title.trim()}\n${description.trim()}`
    if (askedRef.current === key) return
    askedRef.current = key
    controllerRef.current?.abort()
    const controller = new AbortController()
    controllerRef.current = controller
    bugReportService
      .classifyTicket({ title, description }, controller.signal)
      .then((answer) => {
        if (!controller.signal.aborted) setSuggestion(answer)
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || isAbort(error)) return
        logWarn({ context: 'TicketPanel.suggestion', error: String(error) }, 'Ticket suggestion unavailable')
      })
  }, [])

  const reset = useCallback(() => {
    controllerRef.current?.abort()
    askedRef.current = null
    setSuggestion(null)
  }, [])

  useEffect(() => () => controllerRef.current?.abort(), [])

  return { suggestion, request, reset }
}
